import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Global upgrade modal state (accessible outside React tree)
let upgradeModalCallback = null;
export const setUpgradeModalCallback = (cb) => { upgradeModalCallback = cb; };

// Create axios instance with credentials
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for 401 and 403 handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthEndpoint = error.config?.url?.includes('/auth/');
    if (error.response?.status === 401 && !isAuthEndpoint) {
      console.log('Session expired or invalid');
    }
    // Handle 403 feature_not_available — show upgrade modal
    if (error.response?.status === 403) {
      const detail = error.response.data?.detail;
      if (detail && (detail.error === 'feature_not_available' || detail.error === 'limit_exceeded' || detail.error === 'subscription_expired')) {
        // Mark this error as handled by the upgrade modal so callers can skip their own toast
        error._upgradeModalShown = true;
        if (upgradeModalCallback) {
          upgradeModalCallback({
            feature: detail.feature || detail.resource || 'this feature',
            requiredPlan: detail.required_plan ? detail.required_plan.charAt(0).toUpperCase() + detail.required_plan.slice(1) : 'a higher plan',
            message: detail.message || 'Please upgrade your plan to access this feature.',
          });
        }
      }
    }
    return Promise.reject(error);
  }
);

/** Check if an axios error was already handled by the UpgradeModal interceptor */
export const isUpgradeError = (error) => !!error?._upgradeModalShown;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return null;
    }
    
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.log('Auth check failed:', error.message);
      setUser(null);
      localStorage.removeItem('token');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      checkAuth();
    }
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await api.post('/auth/register', { email, password, name });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setUser(userData);
    return userData;
  };

  const processGoogleSession = async (sessionId) => {
    const response = await api.get(`/auth/session?session_id=${sessionId}`);
    const { token, user: userData } = response.data;
    
    if (token) {
      localStorage.setItem('token', token);
    }
    
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Ignore error
    }
    localStorage.removeItem('token');
    setUser(null);
  };

  const updateUser = (updatedFields) => {
    setUser(prev => prev ? { ...prev, ...updatedFields } : prev);
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    processGoogleSession,
    checkAuth,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { api, API, BACKEND_URL };
